// i18n instance — the single source of truth for the active UI locale.
//
// Scope (per the multi-language scaffold): STATIC UI strings + locale-aware
// date/number/currency formatting only. Backend-originated text (agent
// responses, clarification questions, server error strings) and user-provided
// data (matter/session/type names) are intentionally NOT translated here.
//
// To add a language: copy en.json → translate values → add it to LocaleCode,
// AVAILABLE_LOCALES, INTL_LOCALE_TAG, datetimeFormats, numberFormats, and
// messages below — then enable it in ENABLED_NON_DEFAULT_LOCALES (or via the
// VITE_ENABLED_LOCALES env var). Portuguese (pt-BR) is the worked example of
// that flow: fully wired here, but currently disabled by the feature flag.
//
// The active locale is owned by the preference store (which persists it to
// localStorage and restores on load). This module exposes `setI18nLocale` so
// the store can write through to the i18n runtime, and `getActiveLocale` so
// non-component code (e.g. date utilities) can read it without importing Pinia.

import { createI18n } from 'vue-i18n';
import en from './locales/en.json';
import pt from './locales/pt.json';

export type LocaleCode = 'en' | 'pt';

export const DEFAULT_LOCALE: LocaleCode = 'en';

/**
 * Every locale that has a translated catalog wired up (messages + Intl
 * formats). This is the *catalog registry*, not the offered set — a locale
 * can be fully translated here yet still be disabled below via the feature
 * flag. English (the default) is always present.
 */
const AVAILABLE_LOCALES: ReadonlyArray<{ code: LocaleCode; label: string }> = [
    { code: 'en', label: 'English' },
    { code: 'pt', label: 'Português (Brasil)' },
];

/**
 * Feature flag: which NON-DEFAULT languages to actually offer in the UI.
 * English is always enabled and is not listed here.
 *
 * This hardcoded list is the default across every build path (local dev, CI,
 * Docker). Toggle a language by (un)commenting its code, or override the whole
 * list at build time with `VITE_ENABLED_LOCALES` (comma-separated codes, e.g.
 * "pt,fr" — or empty to force English-only). An empty resolved list hides the
 * language switcher entirely (see `isLanguageSwitcherEnabled`).
 */
const ENABLED_NON_DEFAULT_LOCALES: readonly LocaleCode[] = [
    'pt', // Brazilian Portuguese (fully translated)
];

/** Resolve the enabled non-default locales, letting the env var override. */
function resolveEnabledNonDefault(): LocaleCode[] {
    const raw = import.meta.env.VITE_ENABLED_LOCALES;
    const codes =
        typeof raw === 'string' && raw.trim()
            ? raw
                  .split(',')
                  .map((c) => c.trim())
                  .filter(Boolean)
            : ENABLED_NON_DEFAULT_LOCALES;
    // Keep only codes that (a) have a catalog and (b) aren't the default.
    return codes.filter(
        (c): c is LocaleCode =>
            c !== DEFAULT_LOCALE && AVAILABLE_LOCALES.some((l) => l.code === c),
    );
}

/**
 * Locales offered in the language switcher: English first, then each enabled
 * non-default language (in `AVAILABLE_LOCALES` order). This is what the UI
 * should iterate over — never `AVAILABLE_LOCALES` directly.
 */
export const ENABLED_LOCALES: ReadonlyArray<{ code: LocaleCode; label: string }> = (() => {
    const enabled = new Set<LocaleCode>([DEFAULT_LOCALE, ...resolveEnabledNonDefault()]);
    return AVAILABLE_LOCALES.filter((l) => enabled.has(l.code));
})();

/**
 * Whether to show the language switcher at all. With only English enabled
 * there's nothing to switch to, so the dropdown is hidden.
 */
export const isLanguageSwitcherEnabled = ENABLED_LOCALES.length > 1;

/**
 * Narrow an arbitrary string to an ENABLED locale, falling back to the
 * default. A persisted choice for a now-disabled language (e.g. a user who
 * picked Portuguese before it was turned off) resolves back to English.
 */
export function resolveLocale(value: string | null | undefined): LocaleCode {
    return ENABLED_LOCALES.some((l) => l.code === value) ? (value as LocaleCode) : DEFAULT_LOCALE;
}

// Intl-backed formatters keyed by locale. vue-i18n's $d()/$n() select from
// these by name; date utilities also read the active locale for direct Intl
// calls. Keep the BCP-47 tag (e.g. 'en-US') here, not the short locale code.
const INTL_LOCALE_TAG: Record<LocaleCode, string> = {
    en: 'en-US',
    pt: 'pt-BR',
};

/** BCP-47 tag for the given (or active) locale — for Intl.* and toLocale*. */
export function intlTag(locale: LocaleCode = getActiveLocale()): string {
    return INTL_LOCALE_TAG[locale] ?? INTL_LOCALE_TAG[DEFAULT_LOCALE];
}

// Shared shapes — the locale tag (en-US vs pt-BR) drives month names, digit
// grouping, and decimal separators, so both locales register the same options.
const SHORT_DATE = { year: 'numeric', month: 'short', day: 'numeric' } as const;
const LONG_DATE = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
} as const;

// `currency` stays USD for every locale on purpose: monetary values come from
// the backend as USD, so switching the currency *code* by UI language would
// misrepresent amounts — only the grouping/decimal style follows the locale.
const NUMBER_FORMATS = {
    decimal: { style: 'decimal', maximumFractionDigits: 2 },
    integer: { style: 'decimal', maximumFractionDigits: 0 },
    percent: { style: 'percent', maximumFractionDigits: 1 },
    currency: { style: 'currency', currency: 'USD' },
} as const;

const datetimeFormats = {
    en: { short: SHORT_DATE, long: LONG_DATE },
    pt: { short: SHORT_DATE, long: LONG_DATE },
} as const;

const numberFormats = {
    en: NUMBER_FORMATS,
    pt: NUMBER_FORMATS,
} as const;

export const i18n = createI18n({
    legacy: false, // Composition API — t() in <script setup>, $t() in templates.
    locale: DEFAULT_LOCALE, // Overwritten by the preference store at boot.
    fallbackLocale: DEFAULT_LOCALE,
    messages: { en, pt },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    datetimeFormats: datetimeFormats as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    numberFormats: numberFormats as any,
    missingWarn: import.meta.env.DEV,
    fallbackWarn: import.meta.env.DEV,
});

/** Active locale, readable outside of components (no Pinia dependency). */
export function getActiveLocale(): LocaleCode {
    return i18n.global.locale.value as LocaleCode;
}

/** Set the runtime locale. The preference store calls this; it also owns persistence. */
export function setI18nLocale(locale: LocaleCode): void {
    i18n.global.locale.value = locale;
    document.documentElement.setAttribute('lang', locale);
}
